import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';
import { getSessionUserId } from '@shared/stores/authStore';

/**
 * Support — canonical member-facing routes:
 *   - `GET  /support/requests`                   list (member sees own)
 *   - `GET  /support/requests/:id`               detail
 *   - `GET  /support/requests/:id/comments`      thread
 *   - `POST /support/requests/:id/comments`      reply
 *   - `POST /support/requests`                   create
 *   - `GET  /public/support/topics`              topic catalogue
 * Responses use the `{ success, data }` envelope.
 *
 * NB: create goes through the *authenticated* `POST /support/requests` — not
 * `POST /public/support/requests`. The public route runs under `optionalAuth`,
 * which doesn't hydrate the member's owner/customer context, so the request is
 * stored without a `customer_id` and never matches the member-scoped list.
 */

export interface SupportSubTopic {
  subtopic_id: string;
  name: string;
  description?: string;
}

export interface SupportTopic {
  topic_id: string;
  name: string;
  description?: string;
  subtopics: SupportSubTopic[];
}

export interface SupportRequest {
  request_id: string;
  reference: string;
  subject: string;
  topic_name?: string;
  status: 'open' | 'in_progress' | 'awaiting_member' | 'resolved' | 'closed';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  last_message_excerpt?: string;
}

export interface SupportMessage {
  message_id: string;
  author: 'member' | 'staff' | 'system';
  author_name?: string;
  body: string;
  created_at: string;
  attachments?: Array<{ id: string; url: string; name: string; mime: string }>;
}

export interface SupportRequestDetail extends SupportRequest {
  description: string;
  property_id?: string;
  property_label?: string;
  attachments?: Array<{ id: string; url: string; name: string; mime: string }>;
  messages: SupportMessage[];
}

export type SupportPriority = NonNullable<SupportRequest['priority']>;

export interface NewSupportRequest {
  topic_id: string;
  subtopic_id?: string;
  subject: string;
  description: string;
  location_id?: string;
  /** Pinned coordinates — used instead of a property when the issue is elsewhere. */
  latitude?: number;
  longitude?: number;
  priority?: SupportRequest['priority'];
}

/** Tenant API list/detail rows nest the ticket under `request` (member-portal shape). */
type RawSupportRow = {
  request?: Record<string, unknown>;
  topic?: { name?: string } | null;
  subtopic?: { name?: string } | null;
  location?: { location_id?: string; name?: string } | null;
  [key: string]: unknown;
};

type RawCommentRow = {
  comment?: Record<string, unknown>;
  user?: { user_id?: string; full_name?: string | null; email?: string } | null;
};

function toIsoString(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function mapStatus(status: unknown): SupportRequest['status'] {
  const s = String(status ?? 'open');
  if (s === 'pending') return 'awaiting_member';
  if (s === 'open' || s === 'in_progress' || s === 'awaiting_member' || s === 'resolved' || s === 'closed') {
    return s;
  }
  return 'open';
}

function mapPriority(priority: unknown): SupportRequest['priority'] | undefined {
  if (priority == null || priority === '') return undefined;
  const p = String(priority);
  if (p === 'medium') return 'normal';
  if (p === 'low' || p === 'normal' || p === 'high' || p === 'urgent') return p;
  return undefined;
}

function mapSupportRequestRow(row: unknown): SupportRequest {
  const raw = row as RawSupportRow;
  const req = (raw.request ?? row) as Record<string, unknown>;
  const createdAt = toIsoString(req.created_at);
  const updatedAt = toIsoString(req.updated_at) || createdAt;

  return {
    request_id: String(req.request_id ?? ''),
    reference: String(req.reference ?? req.request_number ?? ''),
    subject: String(req.subject ?? ''),
    topic_name: raw.topic?.name ?? (req.topic_name as string | undefined),
    status: mapStatus(req.status),
    priority: mapPriority(req.priority),
    created_at: createdAt,
    updated_at: updatedAt,
    last_message_excerpt: req.last_message_excerpt as string | undefined,
  };
}

function mapSupportRequestDetail(row: unknown): SupportRequestDetail {
  const raw = row as RawSupportRow;
  const req = (raw.request ?? row) as Record<string, unknown>;
  const base = mapSupportRequestRow(row);
  const location = raw.location;

  return {
    ...base,
    description: String(req.description ?? ''),
    property_id: (req.location_id as string | undefined) ?? location?.location_id,
    property_label: location?.name,
    messages: [],
  };
}

function commentAuthorUserId(row: unknown): string {
  const raw = row as RawCommentRow & Record<string, unknown>;
  const comment = (raw.comment ?? row) as Record<string, unknown>;
  const user = raw.user;
  const id =
    comment.user_id ??
    comment.userId ??
    user?.user_id ??
    raw.user_id;
  return id != null && id !== '' ? String(id) : '';
}

function mapSupportCommentRow(row: unknown, memberUserId?: string): SupportMessage {
  const raw = row as RawCommentRow;
  const comment = (raw.comment ?? row) as Record<string, unknown>;
  const user = raw.user;
  const authorUserId = commentAuthorUserId(row);
  const author: SupportMessage['author'] =
    memberUserId && authorUserId && authorUserId === memberUserId ? 'member' : 'staff';

  return {
    message_id: String(comment.comment_id ?? comment.message_id ?? ''),
    author,
    author_name: (user?.full_name ?? comment.author_name) as string | undefined,
    body: String(comment.comment_text ?? comment.body ?? ''),
    created_at: toIsoString(comment.created_at),
  };
}

function currentMemberUserId(): string | undefined {
  return getSessionUserId();
}

export async function fetchSupportRequests(): Promise<SupportRequest[]> {
  const { data } = await tenantApi.get('/support/requests');
  return unwrapList<unknown>(data).map((row) => mapSupportRequestRow(row));
}

interface SupportAttachmentItem {
  id: string;
  url: string;
  name: string;
  mime: string;
  /** Present when the attachment belongs to a thread comment rather than the request. */
  commentId?: string;
}

function mapAttachmentRow(row: unknown): SupportAttachmentItem {
  const a = (row as Record<string, unknown>) ?? {};
  const commentId = a.comment_id ?? a.commentId;
  return {
    id: String(a.attachment_id ?? a.id ?? ''),
    url: String(a.storage_url ?? a.url ?? a.signed_url ?? ''),
    name: String(a.file_name ?? a.name ?? 'attachment'),
    mime: String(a.mime_type ?? a.mime ?? ''),
    commentId: commentId != null && commentId !== '' ? String(commentId) : undefined,
  };
}

export async function fetchSupportRequest(id: string): Promise<SupportRequestDetail> {
  const memberUserId = currentMemberUserId();
  const [detailRes, commentsRes, attachmentsRes] = await Promise.all([
    tenantApi.get(`/support/requests/${id}`),
    tenantApi.get(`/support/requests/${id}/comments`).catch(() => ({ data: { data: [] } })),
    tenantApi.get(`/support/requests/${id}/attachments`).catch(() => ({ data: { data: [] } })),
  ]);
  const detail = mapSupportRequestDetail(unwrap(detailRes.data));
  const messages = unwrapList<unknown>(commentsRes.data).map((row) =>
    mapSupportCommentRow(row, memberUserId),
  );

  // Attachments are returned for the whole request; split request-level ones
  // (shown on the opening message) from comment-linked ones (shown on their
  // bubble, matched by comment_id === message_id).
  const attachments = unwrapList<unknown>(attachmentsRes.data)
    .map(mapAttachmentRow)
    .filter((a) => a.url);
  const requestAttachments = attachments.filter((a) => !a.commentId);
  const byComment = new Map<string, SupportAttachmentItem[]>();
  for (const a of attachments) {
    if (!a.commentId) continue;
    const list = byComment.get(a.commentId) ?? [];
    list.push(a);
    byComment.set(a.commentId, list);
  }
  const messagesWithAttachments = messages.map((msg) => {
    const list = byComment.get(msg.message_id);
    return list && list.length ? { ...msg, attachments: list } : msg;
  });

  return {
    ...detail,
    attachments: requestAttachments,
    messages: messagesWithAttachments,
  };
}

/** Raw `GET /public/support/topics` shapes — DB rows with nested subtopics. */
interface RawSupportSubTopic {
  subtopic_id: string;
  name: string;
  description?: string | null;
}
interface RawSupportTopic {
  topic_id: string;
  name: string;
  description?: string | null;
  subtopics?: RawSupportSubTopic[] | null;
}

export async function fetchSupportTopics(): Promise<SupportTopic[]> {
  const { data } = await tenantApi.get('/public/support/topics');
  return unwrapList<RawSupportTopic>(data).map((topic) => ({
    topic_id: topic.topic_id,
    name: topic.name,
    description: topic.description ?? undefined,
    subtopics: (topic.subtopics ?? []).map((sub) => ({
      subtopic_id: sub.subtopic_id,
      name: sub.name,
      description: sub.description ?? undefined,
    })),
  }));
}

export async function createSupportRequest(payload: NewSupportRequest): Promise<SupportRequest> {
  const apiPayload = {
    ...payload,
    priority:
      payload.priority === 'normal' ? 'medium' : payload.priority,
  };
  const { data } = await tenantApi.post('/support/requests', apiPayload);
  return mapSupportRequestRow(unwrap(data));
}

/**
 * Attach a photo to a just-created support request. The endpoint only accepts
 * uploads shortly after the request is created, so call this right after
 * `createSupportRequest` resolves.
 */
export async function uploadSupportRequestPhoto(requestId: string, file: Blob): Promise<void> {
  const formData = new FormData();
  formData.append('file', file, 'photo.jpg');
  await tenantApi.post(`/public/support/requests/${requestId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * Attach a photo to an existing thread comment.
 * `POST /support/comments/:commentId/attachments` (multipart, field `file`).
 */
export async function uploadSupportCommentAttachment(commentId: string, file: Blob): Promise<void> {
  const ext = file.type === 'image/png' ? 'png' : 'jpg';
  const formData = new FormData();
  formData.append('file', file, `photo.${ext}`);
  await tenantApi.post(`/support/comments/${commentId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function postSupportReply(requestId: string, body: string): Promise<SupportMessage> {
  const { data } = await tenantApi.post(`/support/requests/${requestId}/comments`, {
    comment_text: body,
  });
  return mapSupportCommentRow(unwrap(data), currentMemberUserId());
}
